import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { ShoppingBag, Menu, X, Globe } from "lucide-react";

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const { count, setIsOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: "/shop", label: t.nav.shop, key: "shop" },
    { to: "/shop?filter=new", label: t.nav.newCollection, key: "new" },
    { to: "/about", label: t.nav.about, key: "about" },
    { to: "/lookbook", label: t.nav.lookbook, key: "lookbook" },
    { to: "/community", label: t.nav.community, key: "community" },
    { to: "/journal", label: t.nav.journal, key: "journal" },
    { to: "/contact", label: t.nav.contact, key: "contact" },
  ];

  const transparent = isHome && !scrolled && !mobileOpen;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        transparent
          ? "bg-transparent text-white"
          : "bg-maly-cream/85 backdrop-blur-xl text-maly-navy border-b border-maly-navy/10"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Left — mobile menu / desktop nav */}
          <div className="flex-1 flex items-center">
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 -ml-2"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.slice(0, 4).map((l) => (
                <NavLink
                  key={l.key}
                  to={l.to}
                  data-testid={`nav-${l.key}`}
                  className="font-body text-[11px] tracking-[0.22em] uppercase font-medium link-underline"
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
          </div>

          {/* Center logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="font-display text-2xl lg:text-3xl tracking-[0.35em] font-light"
          >
            MALY
          </Link>

          {/* Right */}
          <div className="flex-1 flex items-center justify-end gap-6 lg:gap-8">
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.slice(4).map((l) => (
                <NavLink
                  key={l.key}
                  to={l.to}
                  data-testid={`nav-${l.key}`}
                  className="font-body text-[11px] tracking-[0.22em] uppercase font-medium link-underline"
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "en" ? "de" : "en")}
              className="hidden sm:flex items-center gap-1.5 font-body text-[11px] tracking-[0.22em] uppercase font-medium"
              aria-label="Toggle language"
            >
              <Globe size={14} className="opacity-70" />
              <span>{lang.toUpperCase()}</span>
            </button>

            <button
              data-testid="open-cart"
              onClick={() => setIsOpen(true)}
              className="relative font-body text-[11px] tracking-[0.22em] uppercase font-medium flex items-center gap-2"
              aria-label="Open cart"
            >
              <ShoppingBag size={18} strokeWidth={1.4} />
              <span className="hidden sm:inline" data-testid="cart-count">({count})</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          mobileOpen ? "max-h-[600px]" : "max-h-0"
        } ${transparent ? "bg-maly-navy/95" : "bg-maly-cream"}`}
      >
        <nav className="px-6 py-8 flex flex-col gap-5">
          {navLinks.map((l) => (
            <NavLink
              key={l.key}
              to={l.to}
              data-testid={`nav-mobile-${l.key}`}
              className="font-display text-2xl tracking-tight"
            >
              {l.label}
            </NavLink>
          ))}
          <div className="pt-6 mt-4 border-t border-current/15 flex items-center gap-6">
            <button
              data-testid="lang-toggle-mobile"
              onClick={() => setLang(lang === "en" ? "de" : "en")}
              className="font-body text-[11px] tracking-[0.22em] uppercase font-medium flex items-center gap-2"
            >
              <Globe size={14} /> {lang.toUpperCase()}
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
}
